import SiteMessagesInstance from '../../components/site-messages'
import loadingSpinner from './components/loading-spinner'
import sort from 'array-sort'

// NIST batch setup, screen 1: the calibration Wi-Fi, entered once for the
// whole batch. The network list comes from the nearest sensor's scan, the
// remembered network (the last successful setup, kept for an hour) is
// preselected, and there is a typed-in fallback for a hidden network.
export class StepBulkWifi {
  constructor (onboarding) {
    this.onboarding = onboarding;
  }

  id = "bulk_wifi";
  title = "Calibration Wi-Fi";

  template = `
  <div class="onboarding-step" data-for="bulk_wifi">

    <p class="onboarding-guide-instruction medium-text-center" data-for="bulk_wifi_intro"></p>

    <p class="onboarding-guide-instruction-smaller medium-text-center">
      Every sensor in the batch joins this network and is named after its own ID.
    </p>

    <div class="onboarding-bulk-wifi__loading">
      <div class="onboarding-ble-nearby__state">${loadingSpinner()}<span>Asking the nearest sensor for the networks it can see...</span></div>
    </div>

    <div class="onboarding-wifi-select"></div>

    <div class="onboarding-wifi-select-use-custom-ssid">
      <div class="switch tiny">
        <input class="switch-input" type="checkbox" id="bulk-wifi-custom-ssid" name="bulk-wifi-custom-ssid" value="1" />
        <label class="switch-paddle" for="bulk-wifi-custom-ssid">
          <span class="show-for-sr">Type the network name</span>
        </label>
      </div>
      <span for="bulk-wifi-custom-ssid">Type the network name instead</span>
    </div>

    <div class="onboarding-wifi-custom-ssid" style="display: none;">
      <label for="bulk_custom_ssid">Network name:</label>
      <input type="text" id="bulk_custom_ssid" name="bulk_custom_ssid" autocomplete="off" aria-autocomplete="none" autocapitalize="off" />
    </div>

    <div class="onboarding-wifi-password">
      <label for="bulk_wifi_password">Wi-Fi password:</label>
      <div class="onboarding-wifi-password-input">
        <div class="onboarding-wifi-password-input__input">
          <input type="text" id="bulk_wifi_password" name="bulk_wifi_password" autocomplete="off" aria-autocomplete="none" autocapitalize="off" />
        </div>
      </div>
      <div class="onboarding-wifi-password-note">(Leave blank if the network has no password)</div>
    </div>

    <button type="button" class="button button--medium" data-for="bulk_wifi_start" disabled></button>

  </div>
  `;

  onBack = () => {
    this.onboarding.goToStep('start_app');
  }

  onActivate = async () => {

    this.onboarding.showBackButton();

    const count = this.onboarding.bulk_devices.length;

    this.$intro.html(`Setting up <b>${count}</b> Temp Stick${count === 1 ? '' : 's'} for recalibration.`);
    this.$startBtn.text(`Start setup of ${count} sensor${count === 1 ? '' : 's'}`);

    this.networks = {};
    this.selected_ssid = '';
    this.remembered = this.readRememberedWifi();

    this.$password.val(this.remembered ? this.remembered.password : '');
    this.$customSsid.val('');
    this.$customToggle.prop('checked', false);
    this.$customWrap.hide();
    this.$select.empty();

    this.updateStartButton();

    await this.loadNetworks();

  }

  onDeactivate = () => {
    this.loading = false;
  }

  bindActions = () => {

    const $step = $(this.onboarding.container).find('.onboarding-step[data-for="bulk_wifi"]');

    this.$intro = $step.find('[data-for="bulk_wifi_intro"]');
    this.$loading = $step.find('.onboarding-bulk-wifi__loading');
    this.$select = $step.find('.onboarding-wifi-select');
    this.$customToggle = $step.find('#bulk-wifi-custom-ssid');
    this.$customWrap = $step.find('.onboarding-wifi-custom-ssid');
    this.$customSsid = $step.find('#bulk_custom_ssid');
    this.$password = $step.find('#bulk_wifi_password');
    this.$startBtn = $step.find('button[data-for="bulk_wifi_start"]');

    this.$select.on('click', '.onboarding-wifi-select__select', e => {
      this.selectNetwork(e.currentTarget.dataset.ssid);
    });

    this.$customToggle.on('change', () => {
      this.$customWrap.toggle(this.$customToggle.is(':checked'));
      this.updateStartButton();
    });

    this.$customSsid.on('input', () => this.updateStartButton());

    this.$startBtn.on('click', () => this.start());

  }

  readRememberedWifi() {

    try {
      const raw = window.localStorage.getItem(this.onboarding.remember_wifi_storage_key);
      if (!raw) {
        return null;
      }
      const data = JSON.parse(raw);
      if (!data || !data.ssid) {
        return null;
      }
      return { ssid: String(data.ssid), password: typeof data.password === 'string' ? data.password : '' };
    } catch (e) {
      return null;
    }

  }

  // The nearest sensor scans for us: connect, ask, hang up.
  async loadNetworks() {

    const bridge = this.onboarding.bleBridge();
    const first = this.onboarding.bulk_devices[0];

    this.loading = true;
    this.$loading.show();

    let networks = [];

    if (bridge && first) {

      const connected = await bridge.connect({ address: first.address, sensorId: first.sensorId, timeoutMs: 15000 });

      if (connected && connected.success) {
        const res = await bridge.request({ type: 'GET', path: '/wirelessNetworks', data: {}, timeoutMs: 25000 });
        if (res && (res.success == true || res.success == 'true')) {
          try {
            const parsed = JSON.parse(String(res.response || '[]'));
            if (Array.isArray(parsed)) {
              networks = parsed;
            }
          } catch (e) {
            networks = [];
          }
        }
        await bridge.disconnect({});
      }

    }

    if (!this.loading) {
      return;   // left the step meanwhile
    }

    this.loading = false;
    this.$loading.hide();

    this.renderNetworks(networks);

    if (networks.length === 0) {
      this.$customToggle.prop('checked', true);
      this.$customWrap.show();
      if (this.remembered) {
        this.$customSsid.val(this.remembered.ssid);
      }
      this.updateStartButton();
    }

  }

  renderNetworks(networks) {

    this.networks = {};

    networks = sort(networks.slice(), 'ssid');

    this.$select.html(networks.map(network => {

      const ssid = String(network.ssid || '').replace(/&#39;/g, "'");
      const key = ssid.replace(/[^a-zA-Z0-9_\-]/g, '');

      this.networks[key] = { ssid, password: network.password };

      return `
        <button class="onboarding-wifi-select__select" type="button" data-ssid="${key}">
          <div class="onboarding-wifi-select__select__text">${ssid}</div>
        </button>
      `;

    }).join(''));

    // Preselect the last network a setup succeeded on.
    if (this.remembered) {
      const key = this.remembered.ssid.replace(/[^a-zA-Z0-9_\-]/g, '');
      if (this.networks[key]) {
        this.selectNetwork(key);
      }
    }

  }

  selectNetwork(key) {

    this.selected_ssid = key;

    this.$select.find('.onboarding-wifi-select__select').removeClass('is-active');
    this.$select.find(`.onboarding-wifi-select__select[data-ssid="${key}"]`).addClass('is-active');

    this.$customToggle.prop('checked', false);
    this.$customWrap.hide();

    this.updateStartButton();

  }

  chosenSsid() {

    if (this.$customToggle.is(':checked')) {
      return String(this.$customSsid.val() || '').trim();
    }

    const network = this.networks[this.selected_ssid];
    return network ? network.ssid : '';

  }

  updateStartButton() {
    this.$startBtn.prop('disabled', this.chosenSsid().length === 0);
  }

  start() {

    const ssid = this.chosenSsid();
    const password = String(this.$password.val() || '').trim();

    if (!ssid) {
      SiteMessagesInstance.addMessage('error', 'Pick or type the calibration Wi-Fi network first');
      SiteMessagesInstance.displayMessages();
      return;
    }

    const network = this.networks[this.selected_ssid];

    if (!this.$customToggle.is(':checked') && network && network.password == 1 && password.length === 0) {
      SiteMessagesInstance.addMessage('error', 'That network needs a password');
      SiteMessagesInstance.displayMessages();
      return;
    }

    // Remember it for the next batch, like a single setup does.
    this.onboarding.pending_remember_wifi = { ssid, password, saved_at: Date.now() };
    this.onboarding.commitRememberedWifi();

    this.onboarding.bulk_wifi = { ssid, password };
    this.onboarding.goToStep('bulk_run');

  }

}
